import Avatar from '-/Custom/Avatar';
import * as Header from '-/Custom/Header';
import {
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import { CommonActions } from 'expo-router/react-navigation';
import Animated from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import PersonManagerSection from '@/components/Person/PersonManagerSection';
import PersonProfileMasthead, {
  usePersonProfileHeaderAnimation,
} from '@/components/Person/PersonProfileMasthead';
import ActionButton from '@/components/UI/ActionButton';
import Button from '@/components/UI/Button';
import ContentBoundary from '@/components/UI/ContentBoundary';
import Section, { SectionHeading } from '@/components/UI/Section';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import type { BadgeAssigneeBoolExp } from '@/gql/scalars';
import useDebounce from '@/hooks/useDebounce';
import useGenerateDm from '@/hooks/useGenerateDm';
import usePaginatedList from '@/hooks/usePaginatedList';
import useUserId from '@/hooks/useUserId';
import {
  GetPersonBadgeAssignments,
  PersonBadgeAssignments,
} from '@/screens/MemberBadgeAssignments';
import { GetEditablePersonProfile } from '@/screens/PersonProfile/PersonProfileQuery';
import { openMemberAssignment } from '@/stores/useMemberAssignmentStore';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';
import { GetPersonSelectedContext } from './PersonContextQueries';
import PersonDetailManagers, {
  GetPersonDetailManagers,
} from './PersonDetailManagers';
import {
  type PersonScope,
  personScopeBadgeWhere,
  personScopeTeamWhere,
} from './PersonScope';
import PersonScopeMembership from './PersonScopeMembership';
import PersonScopeSelect from './PersonScopeSelect';

const GetPersonDetail = graphql(/* GraphQL */ `
  query GetPersonDetail($personId: String!) {
    person_by_pk(id: $personId) {
      id
      name
      color
      avatar_id
      is_managed
      is_support
      contact_access(limit: 1) {
        person_id
      }
      deleted_at
    }
  }
`);

export default function PersonDetail({ personId }: { personId: string }) {
  const { navigate, push } = useRouter();
  const navigation = useNavigation();
  const { teamId, organizationId, profileScope } = useLocalSearchParams<{
    teamId?: string;
    organizationId?: string;
    profileScope?: string;
  }>();
  const scope: PersonScope = profileScope?.startsWith('team:')
    ? { kind: 'team', id: profileScope.slice(5) }
    : profileScope?.startsWith('organization:')
      ? { kind: 'organization', id: profileScope.slice(13) }
      : teamId
        ? { kind: 'team', id: teamId }
        : organizationId
          ? { kind: 'organization', id: organizationId }
          : { kind: 'none' };
  const generateDm = useGenerateDm();
  const userId = useUserId();
  const {
    scrollViewRef,
    scrollY,
    compactHeaderAnimatedProps,
    compactHeaderAnimatedStyle,
  } = usePersonProfileHeaderAnimation();
  const [{ data, fetching, error }, refreshPerson] = useQuery({
    query: GetPersonDetail,
    variables: { personId },
    pause: !personId,
  });
  const person = data?.person_by_pk;
  const [editableProfile] = useQuery({
    query: GetEditablePersonProfile,
    variables: { personId },
    pause: !person?.is_managed,
  });
  const canEditProfile = Boolean(
    person?.is_managed &&
      !editableProfile.error &&
      editableProfile.data?.person.length,
  );

  const badgePagination = usePaginatedList({ limit: 25, scrollViewRef });
  const badgeSearch = useDebounce(DEFAULT_SEARCH_MINIMUM);
  const [scopeInfo] = useQuery({
    query: GetPersonSelectedContext,
    variables: {
      personId,
      teamIds: scope.kind === 'team' ? [scope.id] : [],
      eventIds: scope.kind === 'organization' ? [scope.id] : [],
    },
    pause: !person || scope.kind === 'none',
  });
  const selectedContext =
    scope.kind === 'team'
      ? scopeInfo.data?.teamMemberships.find(
          (membership) => membership.team_id === scope.id,
        )?.team
      : scope.kind === 'organization'
        ? scopeInfo.data?.eventContexts.find(
            ({ event }) => event?.id === scope.id,
          )?.event
        : undefined;
  const scopeTitle =
    scope.kind === 'none'
      ? 'Choose context'
      : (selectedContext?.title ??
        (scopeInfo.fetching ? 'Loading…' : 'Context unavailable'));
  const canAssign =
    scope.kind !== 'none' &&
    !scopeInfo.error &&
    !!selectedContext?.permissions?.assign_badge;
  const assignBadge = (choice: PersonScope) => {
    if (choice.kind === 'none') return;
    openMemberAssignment({
      personId,
      subjectLabel: person?.name ?? 'this person',
      teamId: choice.kind === 'team' ? choice.id : undefined,
      organizationId: choice.kind === 'organization' ? choice.id : undefined,
      testIDPrefix: 'PersonDetail',
    });
    push({ pathname: '/member-action/assign-badge' });
  };
  const startAssignment = () => assignBadge(scope);
  const badgeActions = canAssign ? (
    <ActionButton
      forceLabel
      icon="add-outline"
      label="Assign"
      accessibilityLabel="Assign Badge"
      variant="success"
      testID="PersonDetailAssignBadgeButton"
      onPress={startAssignment}
    />
  ) : null;
  const [managers] = useQuery({
    query: GetPersonDetailManagers,
    variables: { personId, teamWhere: personScopeTeamWhere(scope) },
    pause: !person || scope.kind !== 'team',
  });
  const badgeWhere: BadgeAssigneeBoolExp = {
    person_id: { _eq: personId },
    _and: [personScopeBadgeWhere(scope)],
    _or: [
      { person_id: { _eq: userId || '' } },
      { event: { permissions: { _has_key: 'see_badge' } } },
      { team: { permissions: { _has_key: 'see_badge' } } },
    ],
    ...(badgeSearch.debouncedValue
      ? { badge: { title: { _ilike: `%${badgeSearch.debouncedValue}%` } } }
      : {}),
  };
  const [badges] = useQuery({
    query: GetPersonBadgeAssignments,
    variables: {
      where: badgeWhere,
      limit: badgePagination.limit,
      offset: badgePagination.offset,
    },
    pause: !person || scope.kind === 'none',
  });
  const sectionsLoading =
    scope.kind !== 'none' &&
    [badges, scopeInfo, ...(scope.kind === 'team' ? [managers] : [])].some(
      (result) => !result.data && !result.error,
    );
  const contextSelector = (
    <PersonScopeSelect
      key={personId}
      personId={personId}
      value={scope.kind === 'none' ? '' : `${scope.kind}:${scope.id}`}
      label={scopeTitle}
      onValueChange={(profileScope) => {
        navigation.dispatch(CommonActions.setParams({ profileScope }));
        badgePagination.resetPage();
        badgeSearch.reset();
      }}
    />
  );
  const profileLabel = person?.is_managed ? 'Managed User' : '';
  const canMessage = Boolean(
    person &&
      person.id !== userId &&
      !person.is_managed &&
      !person.deleted_at &&
      (person.contact_access.length > 0 || person.is_support),
  );
  const onMessage = async () => {
    const chatId = await generateDm(personId);
    if (chatId)
      navigate(
        { pathname: '/chat/[chatId]', params: { chatId } },
        { withAnchor: true },
      );
  };
  return (
    <Box flex>
      <Animated.ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.content}
        contentInsetAdjustmentBehavior="automatic"
        keyboardShouldPersistTaps="always"
        testID="PersonDetail"
      >
        <Stack.Screen
          options={{
            headerBackTitle: '',
            headerBackButtonDisplayMode: 'minimal',
            headerTitle: () => (
              <Box style={styles.headerTitle}>
                <Animated.View
                  animatedProps={compactHeaderAnimatedProps}
                  style={[styles.headerLayer, compactHeaderAnimatedStyle]}
                >
                  <Header.MastheadTitleContainer testID="PersonDetailProfile">
                    <Avatar
                      id={person?.avatar_id ?? ''}
                      size="md"
                      color={person?.color ?? ''}
                      name={person?.name ?? ''}
                    />
                    <Box flexShrink={1}>
                      <Text
                        numberOfLines={1}
                        flexShrink={1}
                        size="xl"
                        testID="PersonDetailHeaderTitle"
                      >
                        {person?.name ?? 'Member'}
                      </Text>
                      {profileLabel ? (
                        <Text
                          variant="typographySecondary"
                          size="xs"
                          numberOfLines={1}
                        >
                          {profileLabel}
                        </Text>
                      ) : null}
                    </Box>
                  </Header.MastheadTitleContainer>
                </Animated.View>
              </Box>
            ),
            headerRight: canMessage
              ? () => (
                  <Animated.View
                    animatedProps={compactHeaderAnimatedProps}
                    style={compactHeaderAnimatedStyle}
                  >
                    <Header.End>
                      <Header.ButtonGroup>
                        <Header.Button
                          accessibilityLabel="Message person"
                          onPress={onMessage}
                        >
                          <Icon name="chatbubble-outline" size="2xl" />
                        </Header.Button>
                      </Header.ButtonGroup>
                    </Header.End>
                  </Animated.View>
                )
              : undefined,
            title: '',
          }}
        />
        <ContentBoundary
          data={person}
          loading={fetching}
          error={error}
          onRetry={() => refreshPerson({ requestPolicy: 'network-only' })}
          loadingLabel="Loading person"
          loadingTestID="PersonDetailLoading"
          errorMessage="Could not load this person. Please try again."
          empty={
            <Box p="lg">
              <Text variant="typographySecondary">
                This person is not available to view.
              </Text>
            </Box>
          }
        >
          {(person) => (
            <>
              <PersonProfileMasthead
                minHeight={240}
                avatarSize="2xl"
                surfaceStyle={styles.masthead}
                avatarId={person.avatar_id}
                color={person.color}
                name={person.name ?? 'Member'}
                profileLabel={profileLabel}
                scrollY={scrollY}
                testID="PersonDetailMasthead"
                nameAction={
                  canEditProfile && (
                    <Button
                      appearance="ghost"
                      iconOnly
                      accessibilityLabel="Edit Profile"
                      testID="PersonDetailEditProfile"
                      onPress={() =>
                        navigate({
                          pathname: '/person/[personId]/edit',
                          params: { personId },
                        })
                      }
                    >
                      <Icon
                        name="create-outline"
                        size="2xl"
                        variant="typographySecondary"
                      />
                    </Button>
                  )
                }
                actionContent={
                  canMessage && (
                    <ActionButton
                      forceLabel
                      icon="chatbubble-outline"
                      label="Message"
                      variant="info"
                      onPress={onMessage}
                    />
                  )
                }
              />
              <Box px="lg" space="md" testID="PersonDetailContext">
                <Box style={styles.contextRow}>
                  <Box style={styles.contextSelector}>{contextSelector}</Box>
                  {scope.kind !== 'none' && (
                    <Box style={styles.contextCard}>
                      {!scopeInfo.error && selectedContext && scopeInfo.data ? (
                        <PersonScopeMembership
                          personId={personId}
                          isManaged={!!person.is_managed}
                          scope={scope}
                          data={scopeInfo.data}
                        />
                      ) : (
                        <Box
                          px="md"
                          py="sm"
                          space="xs"
                          accessibilityLabel={
                            scopeInfo.error
                              ? 'Membership unavailable'
                              : 'Loading membership'
                          }
                          accessibilityState={{ busy: !scopeInfo.error }}
                          testID="PersonScopeMembershipLoading"
                        >
                          <Text size="sm" variant="typographySecondary">
                            Role: —
                          </Text>
                          {scope.kind === 'team' && (
                            <Text size="sm" variant="typographySecondary">
                              Participation: —
                            </Text>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
                {scope.kind !== 'none' && scopeInfo.error && (
                  <Text role="alert" variant="error">
                    Could not load this context. Choose another context or try
                    again.
                  </Text>
                )}
              </Box>
              <Box>
                {scope.kind !== 'none' && (
                  <ContentBoundary
                    data={sectionsLoading ? null : true}
                    loading={sectionsLoading}
                    flex={false}
                  >
                    {() => (
                      <Box direction="vertical" space="2xl" px="lg">
                        {scope.kind === 'team' &&
                          !managers.data &&
                          managers.error && (
                            <PersonManagerSection
                              managers={[]}
                              testID="PersonDetailManagers"
                              emptyContent={
                                <Box px="lg" py="sm">
                                  <Text size="sm" role="alert" variant="error">
                                    Could not load managers. Please try again.
                                  </Text>
                                </Box>
                              }
                            />
                          )}
                        {scope.kind === 'team' &&
                          managers.data &&
                          (person.is_managed ||
                            managers.data.person_manager.some(
                              ({ manager }) => manager?.name,
                            )) && (
                            <PersonDetailManagers
                              data={managers.data}
                              personId={person.id}
                              scope={scope}
                              scopeTitle={scopeTitle}
                              isManaged={!!person.is_managed}
                            />
                          )}
                        <Section testID="PersonDetailBadges">
                          {!badges.data && badges.error && (
                            <SectionHeading
                              title="Badges"
                              actionPlacement="inline"
                              actions={badgeActions}
                            />
                          )}
                          {!badges.data && badges.error && (
                            <Text role="alert" variant="error">
                              Could not load badges. Please try again.
                            </Text>
                          )}
                          {badges.data && (
                            <PersonBadgeAssignments
                              key={`${scope.kind}:${scope.id}`}
                              personId={person.id}
                              data={badges.data}
                              search={badgeSearch}
                              pagination={badgePagination}
                              headerActions={badgeActions}
                            />
                          )}
                        </Section>
                      </Box>
                    )}
                  </ContentBoundary>
                )}
              </Box>
            </>
          )}
        </ContentBoundary>
      </Animated.ScrollView>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  contextRow: {
    flexDirection: { base: 'column', md: 'row' },
    alignItems: 'stretch',
    gap: theme.space('md'),
  },
  contextSelector: {
    flex: { base: undefined, md: 3 },
    minWidth: 0,
  },
  masthead: {
    gap: theme.space('md'),
    paddingTop: theme.space('lg'),
    paddingBottom: theme.space('lg'),
  },
  contextCard: {
    flex: { base: undefined, md: 1 },
    minWidth: 0,
    minHeight: theme.size(8) + theme.space('lg') * 2,
    justifyContent: 'center',
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  headerTitle: { height: 44, width: 260, maxWidth: '100%' },
  headerLayer: { position: 'absolute', left: 0, right: 0, top: 0, height: 44 },
  content: {
    flexGrow: 1,
    paddingBottom: theme.space('4xl') * 3,
    rowGap: theme.space('lg'),
  },
}));
